import { useFormStore } from "../store/formStore";
import { useLockStore } from "../store/lockStore";
import { getApi } from "../api";
import { asError, eventBus } from "@optimyl/events";
import { stripInvalidFieldsFromSnapshot } from "../validation/validate";
import { ApiError } from "../types";

/**
 * Detect a "lock lost" save failure and transition the UI to RemovedScreen
 * directly. The backend's `UpdateDraft` returns 409 when the caller's session
 * no longer owns the lock (another user took over and the heartbeat-driven
 * recovery hasn't fired yet). Setting `lockStatus = "removed"` here jumps
 * the recovery — without it, the user would see a generic error toast until
 * the next heartbeat catches up.
 */
function handleSaveError(err: unknown, context: string): void {
  if (err instanceof ApiError && err.status === 409) {
    useLockStore.getState().setLockStatus("removed", useLockStore.getState().lockedBy);
    return;
  }
  eventBus.publish("error", {
    error: asError(err),
    context,
  });
}

/**
 * Persist the current draft to SF. Called from nav handlers (Continue / Back /
 * progress-dot click) — bad/invalid fields are stripped per AC. Returns true
 * on success or no-op (nothing dirty), false on failure so the caller can
 * block navigation and let the user retry.
 */
export async function saveCurrentDraft(): Promise<boolean> {
  const { isDirty, formId, data, formStatus, setSaveStatus } = useFormStore.getState();

  if (!isDirty || !formId || formStatus === "submitted") return true;

  const dataSnapshot = data;
  const validSnapshot = stripInvalidFieldsFromSnapshot(dataSnapshot);
  setSaveStatus("saving");

  try {
    const api = getApi();
    await api.saveDraft(formId, validSnapshot);
    if (useFormStore.getState().formId !== formId) return true;
    setSaveStatus("saved");
    if (useFormStore.getState().data === dataSnapshot) {
      useFormStore.setState({ isDirty: false });
    }
    return true;
  } catch (err) {
    setSaveStatus("error");
    handleSaveError(err, "saveCurrentDraft");
    return false;
  }
}

/**
 * Save only the employee data to SF. Called immediately after employee
 * mutations (add, edit, delete, census upload/remove). Returns true on
 * success, false on failure. Callers can use the return value to re-enable
 * UI controls (e.g. delete buttons).
 *
 * Calls are serialized via a promise chain so that rapid mutations (e.g. add
 * three employees in quick succession) can't have an older request finish
 * after a newer one and silently overwrite the latest state on SF. Each call
 * reads `data.employees` at the moment its slot runs, so the chain always
 * sends the freshest snapshot.
 */
let employeeSaveChain: Promise<boolean> = Promise.resolve(true);

export function saveEmployees(): Promise<boolean> {
  const next = employeeSaveChain.then(() => doSaveEmployees(), () => doSaveEmployees());
  // Keep the chain alive even if a save throws — we already swallow inside doSaveEmployees,
  // but the .then(..., ...) above is defensive in case that ever changes.
  employeeSaveChain = next.catch(() => false);
  return next;
}

async function doSaveEmployees(): Promise<boolean> {
  const { formId, data, formStatus, setSaveStatus } = useFormStore.getState();
  if (!formId || formStatus === "submitted") return true;

  setSaveStatus("saving");

  try {
    const api = getApi();
    await api.saveDraft(formId, { employees: data.employees });
    if (useFormStore.getState().formId !== formId) return true;
    setSaveStatus("saved");
    // Don't clear isDirty — this is a partial save (employees only).
    // saveCurrentDraft handles the full snapshot and isDirty flag.
    return true;
  } catch (err) {
    setSaveStatus("error");
    handleSaveError(err, "saveEmployees");
    return false;
  }
}
